// The volume the rules permit, computed rather than drawn.
//
// Per storey, the plot minus a band along each edge as wide as that edge's
// setback at that storey's cornice height. A setback that grows with height
// therefore steps the upper floors back by itself, and the envelope is a
// stack of footprints rather than one extrusion.
import { MIN_SETBACK } from "../rules/ro.ts";
import { area, capsule, difference, edges, normalise, union, type MultiPoly } from "./geom.ts";
import { setbackAt, type Programme, type Rules, type Site } from "./rules.ts";

export interface EnvelopeStorey {
  index: number; // 0 = ground floor (parter)
  base: number; // floor level, m
  cornice: number; // top of this storey, m
  setbacks: number[]; // per plot edge, m
  footprint: MultiPoly;
  area: number;
}

export interface Envelope {
  plot: MultiPoly;
  plotArea: number;
  storeys: EnvelopeStorey[];
  /** Why the stack stops where it does. */
  limitedBy: "maxHeight" | "maxStoreys" | "empty";
}

export function plotOf(site: Site): MultiPoly {
  if (site.edges.length !== site.boundary.length) {
    throw new Error(`site has ${site.boundary.length} edges but ${site.edges.length} edge kinds`);
  }
  const ring = normalise(site.boundary);
  // normalise may reverse the ring; edge kinds must follow it
  return [[ring]];
}

/** Edge kinds in the order of the normalised (counter-clockwise) ring. */
export function edgeKinds(site: Site) {
  const ring = site.boundary;
  let s = 0;
  for (let i = 0; i < ring.length; i++) {
    const [x1, y1] = ring[i];
    const [x2, y2] = ring[(i + 1) % ring.length];
    s += x1 * y2 - x2 * y1;
  }
  if (s >= 0) return site.edges.slice();
  // reversed ring: edge i of the new ring is old edge n-2-i (mod n)
  const n = site.edges.length;
  return site.edges.map((_, i) => site.edges[(((n - 2 - i) % n) + n) % n]);
}

/** Effective setback on each edge at a given cornice height. */
export function setbacksAt(site: Site, rules: Rules, height: number): number[] {
  return edgeKinds(site).map((k) => Math.max(setbackAt(rules.setbacks[k], height), MIN_SETBACK[k]));
}

export function footprintAt(site: Site, rules: Rules, height: number): { setbacks: number[]; footprint: MultiPoly } {
  const plot = plotOf(site);
  const setbacks = setbacksAt(site, rules, height);
  const bands = edges(plot[0][0]).map(([a, b], i) => capsule(a, b, setbacks[i]));
  return { setbacks, footprint: difference(plot, union(...bands)) };
}

export function envelope(site: Site, rules: Rules, prog: Programme): Envelope {
  const plot = plotOf(site);
  const storeys: EnvelopeStorey[] = [];
  let limitedBy: Envelope["limitedBy"] = "maxHeight";
  for (let i = 0; ; i++) {
    const base = i === 0 ? 0 : prog.groundStoreyHeight + (i - 1) * prog.storeyHeight;
    const cornice = base + (i === 0 ? prog.groundStoreyHeight : prog.storeyHeight);
    if (cornice > rules.maxHeight + 1e-9) { limitedBy = "maxHeight"; break; }
    if (rules.maxStoreys !== undefined && i >= rules.maxStoreys) { limitedBy = "maxStoreys"; break; }
    const { setbacks, footprint } = footprintAt(site, rules, cornice);
    const a = area(footprint);
    if (a < 1) { limitedBy = "empty"; break; }
    storeys.push({ index: i, base, cornice, setbacks, footprint, area: a });
  }
  return { plot, plotArea: area(plot), storeys, limitedBy };
}
